import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  clearEmployeeListsStale,
  fetchEmployees,
  selectProcesses,
} from "../processSlices";
import { EmployeeArgs } from "../types/processTypes";

/**
 * Fetches the employee list for the active tab/filters. When `employeeListsStale`
 * is set (after a mutation), the next fetch uses network-only so other tabs
 * are not served stale Apollo cache.
 */
export function useEmployeeListFetch(listFetchParams: EmployeeArgs) {
  const dispatch = useAppDispatch();
  const { employeeListsStale, listLoading } = useAppSelector(selectProcesses);
  const lastFetchParamsRef = useRef<string>("");

  useEffect(() => {
    const paramsKey = JSON.stringify(listFetchParams);
    const shouldRefresh = employeeListsStale;
    if (!shouldRefresh && paramsKey === lastFetchParamsRef.current) {
      return;
    }
    lastFetchParamsRef.current = paramsKey;

    void dispatch(
      fetchEmployees({
        ...listFetchParams,
        ...(shouldRefresh ? { refreshList: "refresh" } : {}),
      }),
    ).then((action) => {
      // A failed fetch must not mark these params as fetched, or the list
      // silently stays stale until the user picks different filter values.
      if (
        fetchEmployees.rejected.match(action) &&
        lastFetchParamsRef.current === paramsKey
      ) {
        lastFetchParamsRef.current = "";
      }
    });
    if (shouldRefresh) {
      dispatch(clearEmployeeListsStale());
    }
    // Only re-run when tab/filters change; stale is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, listFetchParams]);

  const refreshList = useCallback(() => {
    void dispatch(
      fetchEmployees({ ...listFetchParams, refreshList: "refresh" }),
    );
  }, [dispatch, listFetchParams]);

  return { refreshList, listLoading };
}
